<template>
  <Vue3Dropzone
    v-bind="$attrs"
    class="dropzone-img overflow-hidden"
    :class="{ 'hide-icon': props.hideIcon, 'img-contains': props.display === 'contains', 'has-validation': errorMessage,'is-invalid': errorMessage }"
    :state="errorMessage ? 'error' : 'indeterminate'"
    role="button"
    v-model="pictures"
    v-model:previews="previews"
    :mode="props.mode"
    :multiple="props.multiple"
    :disabled="props.disabled"
    :select-file-strategy="props.multiple ? 'merge' : 'replace'"
    :max-files="props.maxFiles"
    :max-file-size="props.maxFileSize"
    :accept="props.mimeType || (props.type === 'image' ? 'image/png,image/jpeg,image/jpg,image/gif' : (props.type === 'pdf' ? 'application/pdf' : ''))"
    :fileInputId="inputId"
    :height="props.height"
    :showSelectButton="props.showSelectButton"
    :ignoreOriginalPreviews="true"
    :hide-icon="props.hideIcon"
    @fileRemoved="handleFileRemoved"
    @previewRemoved="handleExistingFileRemoved"
    @error="handleError"
    @update:modelValue="valueUpdated"
   >
   <template #title>
     <span class="fs-5 text-muted text-center" v-html="props.introText || (`Déposez ${props.multiple?'vos':'votre'} ${props.type === 'image' ? `image${props.multiple?'s':''}` : (props.type === 'pdf' ? `fichier PDF${props.multiple?'s':''}` : `fichier${props.multiple?'s':''}`)} ici`)"></span>
    </template>
    <template #placeholder-img>
      <span v-if="!props.hideIcon" v-html="props.icon || (`<i class='fa-regular ${props.type === 'image' ? 'fa-image' : (props.type === 'pdf' ? 'fa-file-pdf' : 'fa-file')} fa-3x'></i>`)"></span>
    </template>
    <template #description>
      <small class="text-muted fw-light fst-italic">{{ props.description || `30Mo maximum` }}</small>
    </template>
    <template #button>
      <button
          type="button"
          v-if="props.showSelectButton"
          class="select-file btn btn-primary btn-sm m-2"
      >
        Sélectionner
      </button>
    </template>
    <template #preview="{data: item, removeFile}">
      <div
          class="preview"
          :class="{
          preview__multiple: props.multiple,
          preview__file: item.type === 'file' && item.file && item.file.type && !item.file.type.includes('image/'),
        }"
          :style="{
          width: `${props.imgWidth} !important`,
          height: `${props.imgHeight} !important`,
        }"
          @click.stop
      >
        <!-- For actual File objects -->
        <img
            :src="item.src"
            :alt="item.name || (item.file && item.file.name)"
            v-if="item.type === 'file' && item.file && item.file.type && item.file.type.includes('image/')"
            @click.stop
        />

        <!-- For URL previews -->
        <img
            :src="item.src"
            :alt="item.name"
            v-if="item.type === 'url'"
            @click.stop
        />

        <!-- File type icon for non-images -->
        <Icon
            :name="item.file ? item.file.name.split('.').pop() : 'file'"
            v-if="item.type === 'file' && item.file && item.file.type && (!item.file.type.includes('image/') && !item.file.type.includes('video/'))"
            @click.stop
        />
        <!-- Zoom button -->
        <!-- Todo: Fix to don't show zoom icon on not uploaded file -->
        <button class="img-remove bg-primary" style="right: 95px" v-if="canShowZoom" @click.stop="zoomFile()">
          <i class="fa-solid fa-magnifying-glass-plus p-1"></i>
        </button>

        <!-- Remove button (outside of img-details to be always visible) -->
        <button class="img-remove" v-if="(props.allowSelectOnPreview || props.mode === 'edit')" @click.stop="removeFile(item)">
          <i class="fa-solid fa-close p-1"></i>
        </button>

        <!-- Switch button -->
        <button class="img-remove bg-success" style="right: 50px" v-if="(props.allowSelectOnPreview || props.mode === 'edit')" @click.stop="switchImage">
          <i class="fa-solid fa-arrows-rotate p-1"></i>
        </button>

        <!-- File details overlay -->
        <div class="img-details" v-if="props.allowSelectOnPreview && props.mode !== 'preview'">
          <h2 v-if="item.name || (item.file && item.file.name)">{{ item.name || item.file.name }}</h2>
          <span v-if="item.size || (item.file && item.file.size)">{{ formatSize(item.size || item.file.size) }}</span>
        </div>

        <!-- Preview details overlay for URL previews -->
        <div class="img-details" v-if="item.type === 'url' && props.allowSelectOnPreview">
          <h2 v-if="item.name">{{ item.name }}</h2>
          <span v-if="item.size">{{ formatSize(item.size) }}</span>
        </div>

        <!-- Progress bar for file uploads -->
        <div
            class="progress-bar-container"
            v-if="item.type === 'file' && (item.status === 'pending' || item.status === 'uploading')"
        >
          <div
              class="progress-bar"
              :aria-valuenow="item.progress"
              aria-valuemin="0"
              aria-valuemax="100"
              :style="{ width: `${item.progress}%` }"
          >
            {{ item.progress }}%
          </div>
        </div>
      </div>
    </template>
  </Vue3Dropzone>
  <div class="invalid-feedback">{{ errorMessage }}</div>
</template>

<script setup lang="ts">
import { computed, ref, getCurrentInstance } from 'vue';
import { useField } from 'vee-validate';

import { getBase64FromUrl, IdNotDefined, GuidEmpty, getPictureUrl, getFileUrl } from '@/compositions/helper';
import Icon from '@/components/common/FileUploadIcon.vue';

import { useNotificationsStore } from '@/stores/notificationStore';

const notificationsStore = useNotificationsStore();

const pictures = ref([]);

const props = withDefaults(defineProps<{
  type?: 'image' | 'pdf' | 'file';
  valueKey: AllTypeKeys;
  mode?: 'drop' | 'edit' | 'preview';
  multiple?: boolean;
  disabled?: boolean;
  maxFiles?: number;
  maxFileSize?: number;
  mimeType?: string;
  fileInputId?: string;
  showSelectButton?: boolean;
  height?: string;
  hideIcon?: boolean;
  display?: 'cover' | 'contains';
  displayMaxHeight?: number,
  displayMaxWidth?: number,
  allowSelectOnPreview?: boolean;
  imgWidth?: [number, string],
  imgHeight?: [number, string],
  introText?: string,
  description?: string,
  icon?: string,
}>(), {
  type: 'file',
  mode: 'edit',
  multiple: false,
  disabled: false,
  maxFiles: 1,  // in MB
  maxFileSize: 10,  // in MB
  showSelectButton: true,
  height: 'auto',
  hideIcon: false,
  display: 'contains',
  allowSelectOnPreview: true
});

const inputId = computed(() => {
  return props.fileInputId ? props.fileInputId : `file-input-${getCurrentInstance()?.uid}`;
});

const { value, errorMessage } = useField<FileDb|undefined>(() => props.valueKey);

let touched: boolean = false;
const previews = computed({
  get() {
    return touched ? [] : (
      value?.value
        ? Array.from(
          [
            props.display == 'contains' ? getPictureUrl(value.value.key, props.displayMaxHeight, props.displayMaxWidth) : getPictureUrl(value.value.key),
          ],)
        : []
    );
  },
  set() {
    touched = true;
  }
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const valueUpdated = async (newValues: any[]) =>
{
  if(newValues.length === 0) {
    value.value = undefined;
  } else {
    const b64content = await getBase64FromUrl(newValues[0].src);
    const obj = <FileDb>{
      id: IdNotDefined,
      key: GuidEmpty,
      base64Content: b64content,
      mimeType: newValues[0].file.type,
    };
    value.value = obj;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleFileRemoved = (obj:any) => {
  const toRemoveIndex = previews.value?.findIndex((el) => el === obj.src)
  if (toRemoveIndex !== -1 && toRemoveIndex !== undefined) {
    previews.value?.splice(toRemoveIndex, 1);
  }
}

const handleExistingFileRemoved = () => {
  value.value = undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleError = (error:any) => {
  let customErrorMessage: string | undefined = undefined;
  if (error.type === 'invalid-file-format') {
    customErrorMessage = 'Type de fichier non valide';
  } else if (error.type === 'file-too-large') {
    customErrorMessage = 'Le fichier est trop volumineux';
  } else {
    customErrorMessage = error.type;
  }

  notificationsStore.notifyError(`Erreur lors de l\'ajout du fichier: ${customErrorMessage}`)
}

const formatSize = (size: number) => {
  if (!size) return "Unknown size";
  const i = Math.floor(Math.log(size) / Math.log(1024));
  return (
      (size / Math.pow(1024, i)).toFixed(2) + " " + ["B", "KB", "MB", "GB"][i]
  );
};

const switchImage = () => {
  document.getElementById(inputId.value)?.click();
}

const canShowZoom = computed(() => {
  return (props.allowSelectOnPreview || props.mode === 'edit') && value.value && value.value.id !== IdNotDefined && value.value.key !== GuidEmpty;
});

const zoomFile = () => {
  if (value.value && value.value.key)
    window.open(getFileUrl(value.value.key), '_blank');
}
</script>
<style scoped>
.preview {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}

.preview__multiple {
  height: 150px;
  width: 150px;
}

.preview__file {
  border: 1px dashed rgba(var(--v3-dropzone--primary));
}

.preview__file--error {
  border-color: rgba(var(--v3-dropzone--error)) !important;
}

.preview:hover .img-details,
.preview:hover .img-remove {
  opacity: 1 !important;
  visibility: visible !important;
}

.preview img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.img-details {
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;
  background: rgba(
      var(--v3-dropzone--overlay),
      var(--v3-dropzone--overlay-opacity)
  );
  border-radius: 8px;
  transition: all 0.2s linear;
  -webkit-backdrop-filter: blur(7px);
  backdrop-filter: blur(7px);
  filter: grayscale(1%);
  opacity: 0;
  visibility: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.img-details h2 {
  font-size: 14px;
  font-weight: 400;
  text-align: center;
  color: #fff;
  max-width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 400px) {
  .img-details h2 {
    max-width: 200px;
  }
}

.img-details span {
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: #fff;
}

.img-remove {
  background: rgba(var(--v3-dropzone--error));
  border-radius: 10px;
  border: none;
  padding: 5px;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 10px;
  right: 10px;
  transition: all 0.2s linear;
  z-index: 30; /* Increased z-index to ensure it's on top */
  opacity: 0;
  visibility: hidden;
}

.img-remove:active {
  transform: scale(0.9);
}

.img-remove:hover {
  background: rgba(var(--v3-dropzone--error), 0.8);
}

.progress-bar-container {
  position: absolute;
  bottom: 0;
  background-color: #666;
  border-radius: 5px;
  overflow: hidden;
  width: 100%;
  height: 10px;
}

.progress-bar {
  height: 100%;
  background-color: rgba(var(--v3-dropzone--primary));
  text-align: center;
  font-size: 10px;
  line-height: 10px;
  color: #fff;
  width: 0;
  transition: width 0.5s ease-in-out;
}
</style>
